import React, { useState, useEffect } from "react";
import axios from "axios";
import { LuPencil, LuUserX, LuUserCheck, LuKeyRound } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

const ROLE_LABELS = { management: "Management", admin: "Admin", field_tech: "Field Tech" };

export function UsersSettings() {
  const selfUsername = localStorage.getItem("sfp_username");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("field_tech");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [pwUser, setPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  const loadAll = () => {
    axios.get(`${API}/users/`)
      .then((res) => setUsers(res.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); }, []);

  const openAdd = () => {
    setEditing(null);
    setUsername(""); setFirstName(""); setLastName(""); setEmail(""); setPassword("");
    setRole("field_tech");
    setError("");
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setFirstName(u.first_name); setLastName(u.last_name); setEmail(u.email || "");
    setRole(u.role);
    setError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing) {
      if (!username.trim()) { setError("Username is required."); return; }
      if (!password || password.length < 8) { setError("Password must be at least 8 characters."); return; }
    }
    setSaving(true); setError("");
    try {
      if (editing) {
        await axios.patch(`${API}/users/${editing.id}/`, {
          first_name: firstName, last_name: lastName, email, role,
        });
      } else {
        await axios.post(`${API}/users/`, {
          username: username.trim(), first_name: firstName, last_name: lastName,
          email, password, role,
        });
      }
      setShowModal(false);
      loadAll();
    } catch (err) {
      setError(err.response?.data?.username?.[0] || err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setSaving(false);
  };

  const handleToggleActive = async (u) => {
    const verb = u.is_active ? "Deactivate" : "Reactivate";
    if (!window.confirm(`${verb} ${u.username}?`)) return;
    try {
      await axios.patch(`${API}/users/${u.id}/`, { is_active: !u.is_active });
      loadAll();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Something went wrong.");
    }
  };

  const openResetPassword = (u) => {
    setPwUser(u);
    setNewPassword("");
    setPwError("");
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { setPwError("Password must be at least 8 characters."); return; }
    setPwSaving(true); setPwError("");
    try {
      await axios.post(`${API}/users/${pwUser.id}/reset-password/`, { new_password: newPassword });
      setPwUser(null);
    } catch (err) {
      setPwError(err.response?.data?.error || "Something went wrong.");
    }
    setPwSaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Users</div>
        <button style={styles.addButton} onClick={openAdd}>+ Add User</button>
      </div>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading…</div>
        ) : users.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: colors.slate, fontSize: "14px" }}>
            No users yet. Click "+ Add User" to get started.
          </div>
        ) : (
          <div style={styles.card}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1.3fr 1.3fr 1fr 90px 110px",
              padding: "8px 20px",
              backgroundColor: "#f8fafc",
              borderBottom: `1px solid ${colors.border}`,
              fontSize: "11px", fontWeight: 700, color: colors.slate,
              textTransform: "uppercase", letterSpacing: "0.4px",
            }}>
              <div>Username</div>
              <div>Name</div>
              <div>Email</div>
              <div>Role</div>
              <div>Status</div>
              <div></div>
            </div>
            {users.map((u) => {
              const isSelf = u.username === selfUsername;
              return (
                <div key={u.id} style={{
                  display: "grid", gridTemplateColumns: "1fr 1.3fr 1.3fr 1fr 90px 110px",
                  padding: "11px 20px", borderBottom: `1px solid ${colors.border}`,
                  alignItems: "center", backgroundColor: colors.white,
                }}>
                  <div style={{ fontSize: "14px", color: colors.text, fontWeight: 500 }}>{u.username}</div>
                  <div style={{ fontSize: "14px", color: colors.text }}>{u.first_name} {u.last_name}</div>
                  <div style={{ fontSize: "13px", color: colors.slate }}>{u.email || "—"}</div>
                  <div style={{ fontSize: "13px", color: colors.text }}>{ROLE_LABELS[u.role] || u.role}</div>
                  <div>
                    <span style={{
                      fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "20px",
                      backgroundColor: u.is_active ? "#dcfce7" : "#f1f5f9",
                      color: u.is_active ? "#15803d" : colors.slate,
                    }}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                    <button onClick={() => openEdit(u)} title="Edit" style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: colors.slate, padding: "4px", borderRadius: "4px",
                      display: "flex", alignItems: "center",
                    }}><LuPencil size={15} /></button>
                    <button onClick={() => openResetPassword(u)} title="Reset password" style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: colors.slate, padding: "4px", borderRadius: "4px",
                      display: "flex", alignItems: "center",
                    }}><LuKeyRound size={15} /></button>
                    <button
                      onClick={() => !isSelf && handleToggleActive(u)}
                      disabled={isSelf}
                      title={isSelf ? "You cannot deactivate your own account" : u.is_active ? "Deactivate" : "Reactivate"}
                      style={{
                        background: "none", border: "none",
                        cursor: isSelf ? "default" : "pointer",
                        color: isSelf ? "#cbd5e1" : u.is_active ? "#dc2626" : "#15803d",
                        padding: "4px", borderRadius: "4px",
                        display: "flex", alignItems: "center",
                      }}
                    >
                      {u.is_active ? <LuUserX size={15} /> : <LuUserCheck size={15} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <SimpleModal
          title={editing ? `Edit: ${editing.username}` : "New User"}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          saving={saving}
          error={error}
        >
          {!editing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Username *</label>
              <input style={inputStyle} value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>First Name</label>
              <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last Name</label>
              <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {!editing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Password *</label>
              <input style={inputStyle} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
          )}
          <div style={fieldStyle}>
            <label style={labelStyle}>Role *</label>
            <select style={inputStyle} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="field_tech">Field Tech</option>
              <option value="admin">Admin</option>
              <option value="management">Management</option>
            </select>
          </div>
        </SimpleModal>
      )}

      {pwUser && (
        <SimpleModal
          title={`Reset Password: ${pwUser.username}`}
          onClose={() => setPwUser(null)}
          onSave={handleResetPassword}
          saving={pwSaving}
          error={pwError}
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>New Password *</label>
            <input
              style={inputStyle}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
          </div>
        </SimpleModal>
      )}
    </div>
  );
}
